import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../utils/theme';
import { device } from '../utils/mediaQueries';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { projectsData } from '../utils/constants';

const ProjectsSection = styled.section`
  padding: 80px 20px;
  background: ${theme.background};
  color: ${theme.text};
  width: 100%;

  ${device.tablet} {
    padding: 100px 40px;
  }

  ${device.laptop} {
    padding: 120px 60px;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  text-align: center;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.75rem, 5vw, 3rem);
  margin-bottom: 1rem;
  color: ${theme.white};
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Subtitle = styled(motion.p)`
  color: ${theme.secondary};
  font-family: ${theme.fontMono};
  font-size: 0.9rem;
  margin-bottom: 3rem;
  letter-spacing: 0.05em;
  
  ${device.tablet} {
    font-size: 1rem;
    margin-bottom: 3.5rem;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  ${device.tablet} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  ${device.laptop} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ProjectCard = styled(motion.div)`
  background: ${theme.cardBackground};
  border-radius: 12px;
  padding: 1.25rem;
  transition: ${theme.transition};
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.05);
  text-align: left;
  
  ${device.tablet} {
    padding: 1.5rem;
  }
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 255, 255, 0.12);
    
    .folder-icon {
      color: ${theme.white};
    }
    
    h3 {
      color: ${theme.white};
    }
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const FolderIcon = styled.div`
  color: ${theme.secondary};
  font-size: 32px;
  transition: ${theme.transition};

  ${device.tablet} {
    font-size: 36px;
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  
  a {
    color: ${theme.text};
    font-size: 1.1rem;
    transition: ${theme.transition};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    
    &:hover {
      color: ${theme.white};
    }
  }
`;

const ProjectTitle = styled.h3`
  color: ${theme.lightGray};
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  transition: ${theme.transition};
  
  ${device.tablet} {
    font-size: 1.25rem;
  }
`;

const ProjectDescription = styled.p`
  color: ${theme.text};
  font-size: 0.85rem;
  margin-bottom: 1.25rem;
  flex-grow: 1;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${device.tablet} {
    font-size: 0.9rem;
  }
`;

const TechList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  margin: 0;
  list-style: none;
  gap: 0.4rem;
  
  li {
    font-family: ${theme.fontMono};
    font-size: 0.7rem;
    color: ${theme.secondary};
    background: rgba(255, 255, 255, 0.06);
    padding: 0.2rem 0.6rem;
    border-radius: 20px;

    ${device.tablet} {
      font-size: 0.75rem;
      padding: 0.25rem 0.75rem;
    }
  }
`;

const ShowMoreButton = styled(motion.button)`
  margin-top: 3rem;
  padding: 0.8rem 2rem;
  background: transparent;
  color: ${theme.white};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  font-family: ${theme.fontMono};
  font-size: 0.9rem;
  cursor: pointer;
  transition: ${theme.transition};
  -webkit-tap-highlight-color: transparent;

  &:hover {
    border-color: ${theme.white};
    background: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }

  ${device.tablet} {
    margin-top: 3.5rem;
    padding: 0.9rem 2.5rem;
    font-size: 0.95rem;
  }
`;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    // Using static data to ensure all specific projects are shown correctly
    setProjects(projectsData);
    setLoading(false);
  }, []);

  const handleShowMore = () => {
    setVisibleCount(prev => prev === projects.length ? 6 : projects.length);
  };

  return (
    <ProjectsSection id="projects">
      <Container>
        <Subtitle data-aos="fade-up">
          My work
        </Subtitle>
        <SectionTitle data-aos="fade-up" data-aos-delay="100">
          Featured Projects
        </SectionTitle>
        <ProjectsGrid>
          {loading ? (
            <p>Loading projects...</p>
          ) : (
            projects.slice(0, visibleCount).map((project, index) => (
              <ProjectCard
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (index % 6) * 0.08 }}
              >
                <ProjectHeader>
                  <FolderIcon className="folder-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                  </FolderIcon>
                  <ProjectLinks>
                    <a href={project.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub Link">
                      <FaGithub />
                    </a>
                    {project.link !== project.github && (
                      <a href={project.link} target="_blank" rel="noopener noreferrer" aria-label="External Link">
                        <FaExternalLinkAlt />
                      </a>
                    )}
                  </ProjectLinks>
                </ProjectHeader>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription title={project.description}>{project.description}</ProjectDescription>
                <TechList>
                  {project.tech.map((tech, i) => (
                    <li key={i}>{tech}</li>
                  ))}
                </TechList>
              </ProjectCard>
            ))
          )}
        </ProjectsGrid>
        
        {!loading && projects.length > 6 && (
          <ShowMoreButton 
            onClick={handleShowMore}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {visibleCount === projects.length ? 'Show Less' : 'Show More'}
          </ShowMoreButton>
        )}
      </Container>
    </ProjectsSection>
  );
};

export default Projects;